import { useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { Loader2, AlertCircle, CheckCircle, Home } from 'lucide-react'

import { AuthService } from '@/services/auth-service'
import type { Cliente } from '@/types/auth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Logo } from '@/components/ui/logo'
import { DotPattern } from '@/components/ui/dot-pattern'
import { ThemeToggle } from '@/components/theme-toggle'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/auth/$hub_login')({
  component: AuthMagicLinkComponent,
})

type AuthStatus = 'loading' | 'success' | 'error'

type MagicLinkCliente = {
  id?: number | string | null
  nome?: string | null
  email?: string | null
  [key: string]: unknown
}

type MagicLinkResponse = {
  ok: boolean
  cliente?: MagicLinkCliente
  sessionData?: {
    issuedAt?: string
    ttlSeconds?: number
  }
  error?: string
}

function AuthMagicLinkComponent() {
  const navigate = useNavigate()
  const { hub_login } = useParams({ from: '/auth/$hub_login' })
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [clienteNome, setClienteNome] = useState<string | null>(null)

  const mountedRef = useRef(true)
  const timeoutsRef = useRef<number[]>([])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      timeoutsRef.current.forEach((id) => window.clearTimeout(id))
      timeoutsRef.current = []
    }
  }, [])

  useEffect(() => {
    const delay = (ms: number) =>
      new Promise<void>((resolve) => {
        const id = window.setTimeout(() => {
          resolve()
        }, ms)
        timeoutsRef.current.push(id)
      })

    const scheduleRedirect = (to: string, ms: number) => {
      const id = window.setTimeout(() => {
        if (!mountedRef.current) return
        navigate({ to })
      }, ms)
      timeoutsRef.current.push(id)
    }

    const controller = new AbortController()

    const run = async () => {
      const uuid = String(hub_login ?? '').trim().toLowerCase()
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

      if (!uuid || !uuidRegex.test(uuid)) {
        if (!mountedRef.current) return
        setStatus('error')
        setErrorMessage('Link inválido')
        scheduleRedirect('/', 3000)
        return
      }

      try {
        const resp = await fetch('/api/auth-magic-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hub_login }),
          signal: controller.signal,
        })

        const contentType = resp.headers.get('content-type') || ''
        const data: MagicLinkResponse | null = contentType.includes('application/json')
          ? await resp.json()
          : null

        if (resp.ok && data?.ok && data.cliente) {
          const cliente = data.cliente as MagicLinkCliente

          const rawId = cliente.id ?? cliente.id_cliente ?? cliente.cliente_id ?? cliente.user_id ?? cliente.uid ?? null
          let normalizedId: string | number | null = null

          if (typeof rawId === 'string') {
            const trimmed = rawId.trim()
            normalizedId = trimmed.length > 0 ? trimmed : null
          } else if (typeof rawId === 'number' && Number.isFinite(rawId)) {
            normalizedId = rawId
          }

          const normalizedNome = String(cliente.nome ?? cliente.name ?? cliente.full_name ?? '').trim()
          const normalizedEmail = String(cliente.email ?? cliente.email_address ?? '').trim()

          const hasId =
            typeof normalizedId === 'string'
              ? normalizedId.length > 0
              : typeof normalizedId === 'number' && Number.isFinite(normalizedId) && normalizedId > 0
          const hasEmail = normalizedEmail.length > 0

          if (!hasId || !hasEmail) {
            if (!mountedRef.current) return
            setStatus('error')
            setErrorMessage('Dados de cliente inválidos retornados pela API')
            scheduleRedirect('/', 3000)
            return
          }

          const apiHubLogin = typeof cliente.hub_login === 'string' ? cliente.hub_login.trim().toLowerCase() : null
          const sessionHubLogin = apiHubLogin && apiHubLogin.length > 0 ? apiHubLogin : uuid

          const normalized: Cliente = {
            id: normalizedId!,
            nome: normalizedNome,
            email: normalizedEmail,
            hub_login: sessionHubLogin,
          }

          try {
            AuthService.salvarSessao(normalized)
          } catch (sessionError) {
            console.error('Erro ao salvar sessão do cliente', sessionError)
          }

          if (!mountedRef.current) return
          setClienteNome(normalized.nome ?? '')
          setErrorMessage(null)
          setStatus('success')

          await delay(1500)
          if (!mountedRef.current) return
          navigate({ to: '/dashboard' })
          return
        }

        const errorCode = data?.error || ''
        if (resp.status === 404 || errorCode === 'HUB_LOGIN_NOT_FOUND') {
          if (!mountedRef.current) return
          setStatus('error')
          setErrorMessage('Link não encontrado ou expirado')
          scheduleRedirect('/', 3000)
          return
        }

        if (resp.status === 400 || errorCode === 'INVALID_UUID_FORMAT') {
          if (!mountedRef.current) return
          setStatus('error')
          setErrorMessage('Link inválido')
          scheduleRedirect('/', 3000)
          return
        }

        if (!mountedRef.current) return
        setStatus('error')
        setErrorMessage('Erro ao processar autenticação. Tente novamente.')
        scheduleRedirect('/', 3000)
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return
        }
        console.error('Erro ao autenticar via magic link', error)
        if (!mountedRef.current) return
        setStatus('error')
        setErrorMessage('Erro de conexão. Verifique sua internet.')
        scheduleRedirect('/', 3000)
      }
    }

    run()

    return () => {
      controller.abort()
    }
  }, [hub_login, navigate])

  const handleReturnHome = () => {
    timeoutsRef.current.forEach((id) => window.clearTimeout(id))
    timeoutsRef.current = []
    navigate({ to: '/' })
  }

  const cardClass = cn(
    'relative w-full max-w-md animate-in fade-in duration-500 slide-in-from-bottom-2',
    'backdrop-blur-md bg-white/98 dark:bg-background/95 border-2 border-neutral-200 dark:border-border/50',
    'shadow-[0_8px_30px_rgba(0,0,0,0.12)] dark:shadow-2xl'
  )

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden bg-gradient-to-br from-neutral-50 via-blue-50/30 to-purple-50/30 dark:from-background dark:via-background dark:to-background">
      <DotPattern className="text-blue-200/30 dark:text-muted-foreground/10" glow cr={1.5} />
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <Card
        className={cardClass}
        role={status === 'loading' ? 'status' : status === 'error' ? 'alert' : undefined}
        aria-live={status === 'error' ? 'assertive' : 'polite'}
      >
        {status === 'loading' && (
          <>
            <CardHeader className="space-y-6 text-center">
              <Logo className="mx-auto h-16" />
              <div className="space-y-2">
                <CardTitle>Autenticando...</CardTitle>
                <CardDescription>Aguarde enquanto validamos seu acesso</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 animate-spin text-primary" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">Carregando seus livros...</p>
            </CardContent>
          </>
        )}

        {status === 'success' && (
          <>
            <CardHeader className="space-y-6 text-center animate-in fade-in duration-500">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" aria-hidden="true" />
              <div className="space-y-2">
                <CardTitle>Acesso confirmado!</CardTitle>
                <CardDescription>
                  {clienteNome ? `Bem-vindo(a), ${clienteNome}!` : 'Redirecionando para seus livros...'}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">Você será redirecionado em instantes...</p>
              <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" aria-hidden="true" />
            </CardContent>
          </>
        )}

        {status === 'error' && (
          <>
            <CardHeader className="space-y-6 text-center animate-in fade-in duration-500">
              <AlertCircle className="w-16 h-16 text-rose-500 mx-auto" aria-hidden="true" />
              <div className="space-y-2">
                <CardTitle>Ops! Algo deu errado</CardTitle>
                <CardDescription>{errorMessage || 'Não foi possível autenticar'}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                Você será redirecionado para a página inicial em instantes.
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                onClick={handleReturnHome}
              >
                <Home className="w-4 h-4" aria-hidden="true" />
                Voltar agora
              </Button>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  )
}
